import { apiClient } from '@/shared/lib/axios';

export interface CreateClinicUserPayload {
  clinic_id: number;
  /** 権限: 一般権限 (0) / 管理者権限 (1) */
  role: number;
  login_id: string;
  password: string;
  name: string;
  email?: string | null;
  available_from?: string | null;
  available_to?: string | null;
}

export interface UpdateClinicUserPayload {
  /** 権限: 一般権限 (0) / 管理者権限 (1) */
  role?: number;
  login_id?: string;
  password?: string | null;
  name?: string;
  email?: string | null;
  available_from?: string | null;
  available_to?: string | null;
}

/** 「SSO連携済み」 label vs 「SSO連携」 retry button (BD "SSO Clinic admin" mục 2.0/3.0). */
export type ClinicUserSsoLinkStatus = 'none' | 'unlinked' | 'pending' | 'linked';

export interface ClinicUserResponse {
  id: number;
  clinic_id: number;
  role: number;
  login_id: string;
  password: string | null;
  name: string;
  email: string | null;
  available_from: string | null;
  available_to: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  sso_link_status?: ClinicUserSsoLinkStatus;
}

export interface ClinicUserApiResponse {
  success: boolean;
  message: string;
  data: ClinicUserResponse;
}

// Alias for backward compatibility
export type CreateClinicUserApiResponse = ClinicUserApiResponse;

/**
 * Get a clinic user by ID (admin portal).
 */
export const getClinicUser = async (
  siteType: string,
  id: number
): Promise<ClinicUserApiResponse> => {
  const response = await apiClient.get<ClinicUserApiResponse>(
    `/admin/${siteType}/clinic-users/${id}`
  );
  return response.data;
};

/**
 * Create a new clinic user (admin portal).
 */
export const createClinicUser = async (
  siteType: string,
  data: CreateClinicUserPayload
): Promise<ClinicUserApiResponse> => {
  const payload = {
    ...data,
    email: data.email && data.email.trim() !== '' ? data.email.trim() : null,
    available_from: data.available_from && data.available_from.trim() !== '' ? data.available_from : null,
    available_to: data.available_to && data.available_to.trim() !== '' ? data.available_to : null,
  };
  const response = await apiClient.post<ClinicUserApiResponse>(
    `/admin/${siteType}/clinic-users`,
    payload
  );
  return response.data;
};

/**
 * Update a clinic user (admin portal).
 */
export const updateClinicUser = async (
  siteType: string,
  id: number,
  data: UpdateClinicUserPayload
): Promise<ClinicUserApiResponse> => {
  const payload = {
    ...data,
    email: data.email && data.email.trim() !== '' ? data.email.trim() : null,
    available_from: data.available_from && data.available_from.trim() !== '' ? data.available_from : null,
    available_to: data.available_to && data.available_to.trim() !== '' ? data.available_to : null,
    // Only include password if it's provided and not empty
    ...(data.password && data.password.trim() !== '' ? { password: data.password } : {}),
  };
  const response = await apiClient.put<ClinicUserApiResponse>(
    `/admin/${siteType}/clinic-users/${id}`,
    payload
  );
  return response.data;
};

export interface ClinicAdminApiResponse {
  success: boolean;
  message: string;
  data: {
    admin_clinic_user_id: number | null;
    admin_name: string | null;
  };
}

/**
 * Get the 管理者権限 user of a clinic, if any (admin portal).
 */
export const getClinicAdmin = async (
  siteType: string,
  clinicId: number
): Promise<ClinicAdminApiResponse> => {
  const response = await apiClient.get<ClinicAdminApiResponse>(
    `/admin/${siteType}/clinics/${clinicId}/clinic-admin`
  );
  return response.data;
};

// ── List / CSV ────────────────────────────────────────────────

export type ClinicUserListParams = {
  search_login_id?: string;
  search_name?: string;
  search_clinic_name?: string;
  site_types?: string[];
  sort?: [string, 'asc' | 'desc'];
  page?: number;
  per_page?: number;
};

export interface ClinicUserListItem {
  id: number;
  display_id: string;
  name: string;
  clinic_name: string;
  site_type?: string;
}

export interface ClinicUserListMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface ClinicUserListResponse {
  success: boolean;
  data: {
    data: ClinicUserListItem[];
  };
  meta: ClinicUserListMeta;
  message?: string;
}

/**
 * Get paginated clinic user list (admin portal).
 */
export const getClinicUsers = async (
  siteType: string,
  params: ClinicUserListParams
): Promise<ClinicUserListResponse> => {
  const response = await apiClient.get<ClinicUserListResponse>(
    `/admin/${siteType}/clinic-users`,
    { params }
  );
  return response.data;
};

/**
 * Download clinic users as CSV (admin portal).
 */
export const downloadClinicUsersCsv = async (
  siteType: string,
  params: ClinicUserListParams
): Promise<void> => {
  const response = await apiClient.get(`/admin/${siteType}/clinic-users/csv`, {
    params,
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'text/csv;charset=Shift_JIS;' });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `clinic_user${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/** The endpoint's success body plus the message that explains WHICH success it was. */
export interface ClinicUserSsoLinkResponse extends ClinicUserSsoLinkResult {
  message: string;
}

export interface ClinicUserSsoLinkResult {
  /**
   * How many 開示 sites this 会員 is reachable on. 0 is a real, non-error answer — the member has
   * not signed in to 開示 yet, so P1 has nothing to point the P3 leg at (see
   * `ClinicAdminSsoIdentityLinker`). The caller's own toast already carries the Japanese message
   * for that case; this count is not meant to be displayed on its own.
   */
  p3_site_count: number;
}

/**
 * 「SSO連携」 retry button on 治療院詳細's 治療院ユーザー table (BD "SSO Clinic admin" mục 3.0).
 *
 * Unlike the 管理者 lane's retry, this one never needs a password from the caller:
 * `clinic_users.password` is plaintext (backward compatibility with RA2), so the server always has
 * one to retry with. No `password` argument here on purpose — the button is a single click.
 *
 * Does NOT return the updated clinic user: the endpoint's success body is `{p3_site_count}`, not
 * `ClinicUserResource`. `sso_link_status` for the badge comes back through the clinic-detail query
 * the caller refetches in `onSuccess`, not from this response.
 */
export const retryClinicUserSsoLink = async (
  siteType: string,
  id: number
): Promise<ClinicUserSsoLinkResponse> => {
  const response = await apiClient.post<{ success: boolean; message: string; data: ClinicUserSsoLinkResult }>(
    `/admin/${siteType}/clinic-users/${id}/sso-link`,
    {}
  );
  // The MESSAGE comes back with the count, deliberately. This endpoint has two success shapes —
  // both legs written, or P1 written while 開示 has not received the clinic yet — and it says
  // which one in `message`. Dropping it here forced the caller to invent its own wording, which
  // is how a half-done link came to announce 「SSO連携しました。」 (QC, clinic 123656).
  return { ...response.data.data, message: response.data.message };
};
